import { Component, Injector, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AppComponentBase } from '@shared/common/app-component-base';
import { appModuleAnimation } from '@shared/animations/routerTransition';
import { MediaCenterServiceProxy, MediaCenterCategory, GetMediaCenterForViewDto, MediaLibraryServiceProxy } from '@shared/service-proxies/service-proxies';
import { finalize, catchError } from 'rxjs/operators';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { of } from 'rxjs';
import { AppConsts } from '@shared/AppConsts';
import { HttpClient } from '@angular/common/http';

@Component({
    templateUrl: './media-center-list.component.html',
    animations: [appModuleAnimation()]
})
export class MediaCenterListComponent extends AppComponentBase implements OnInit {

    mediaCenters: GetMediaCenterForViewDto[] = [];
    loading = false;
    category: MediaCenterCategory;
    categoryName: string;
    filterText = '';
    searchError = false;

    // Category constants
    readonly CAT_NEWS = 1;
    readonly CAT_EVENTS = 2;
    readonly CAT_LIBRARY = 3;

    // Pagination
    first = 0;
    maxResultCount = 9;
    totalCount = 0;

    imageDataMap: { [key: number]: SafeResourceUrl } = {};
    videoDataMap: { [key: number]: SafeResourceUrl } = {};

    // Gallery lightbox state (for media library category)
    galleryOpen = false;
    galleryIndex = 0;
    galleryItems: GetMediaCenterForViewDto[] = [];

    constructor(
        injector: Injector,
        private _mediaCenterService: MediaCenterServiceProxy,
        private _mediaLibraryService: MediaLibraryServiceProxy,
        private _activatedRoute: ActivatedRoute,
        private _sanitizer: DomSanitizer,
        private _http: HttpClient
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.setCategoryFromRoute();
        // Also subscribe to params in case of sharing component but different reuse strategy
        this._activatedRoute.data.subscribe(data => {
            if (data.type) {
                this.setCategory(data.type);
                this.getMediaCenters();
            }
        });

        // Fallback or override if using :type param
        this._activatedRoute.params.subscribe(params => {
            if (params['type']) {
                this.setCategory(params['type']);
                this.getMediaCenters();
            }
        });
    }

    setCategoryFromRoute() {
        const snapshotData = this._activatedRoute.snapshot.data;
        if (snapshotData && snapshotData.type) {
            this.setCategory(snapshotData.type);
            this.getMediaCenters();
        }
    }

    setCategory(type: string) {
        // ... switch logic remains same
        switch (type) {
            case 'news':
                this.category = this.CAT_NEWS;
                this.categoryName = 'الأخبار';
                break;
            case 'events':
                this.category = this.CAT_EVENTS;
                this.categoryName = 'الفعاليات';
                break;
            case 'library':
                this.category = this.CAT_LIBRARY;
                this.categoryName = 'مركز الوسائط';
                break;
            default:
                this.category = this.CAT_NEWS;
                this.categoryName = 'الأخبار';
        }
    }

    onFilterChange(): void {
        // Clear the error as soon as the user starts editing
        this.searchError = false;
    }

    onSearch(): void {
        const trimmed = this.filterText.trim();
        // Allow empty (show all) or require at least 3 chars
        if (trimmed.length > 0 && trimmed.length < 3) {
            this.searchError = true;
            return;
        }
        this.searchError = false;
        this.first = 0;
        this.getMediaCenters();
    }

    clearSearch(): void {
        this.filterText = '';
        this.searchError = false;
        this.first = 0;
        this.getMediaCenters();
    }

    getMediaCenters() {
        this.loading = true;
        this._mediaCenterService.getAllPublished(
            this.filterText,
            this.category,
            undefined, // sorting
            this.first,
            this.maxResultCount
        ).pipe(
            catchError(error => {
                console.error('Failed to load media centers', error);
                this.mediaCenters = [];
                this.totalCount = 0;
                return of({ items: [], totalCount: 0 });
            }),
            finalize(() => this.loading = false)
        ).subscribe(result => {
            this.mediaCenters = result.items || [];
            this.totalCount = result.totalCount || 0;
            // Images load via direct URL (imageAttachmentUrls) — no extra requests needed
        });
    }


    getImage(item: GetMediaCenterForViewDto): string {
        // Use the direct URL the API provides — no extra HTTP call needed
        return item.imageAttachmentUrls || '/assets/common/images/logo-on-dark.png';
    }

    getVideo(item: GetMediaCenterForViewDto): SafeResourceUrl {
        return this.videoDataMap[item.mediaCenter.id] || '';
    }

    isVideo(item: GetMediaCenterForViewDto): boolean {
        return !!item.mediaCenter.vidioAttachments;
    }

    onPageChange(event: any) { // Pagination event from PrimeNG
        this.first = event.first;
        this.getMediaCenters();
    }

    // Gallery lightbox methods
    openGallery(index: number): void {
        this.galleryItems = this.mediaCenters;
        this.galleryIndex = index;
        this.galleryOpen = true;
        document.body.style.overflow = 'hidden';
        // Lazily load the video for this item only when the lightbox is opened
        this.loadVideoForGalleryItem(index);
    }

    loadVideoForGalleryItem(index: number): void {
        const item = this.galleryItems[index];
        if (!item) { return; }
        const id = item.mediaCenter.id;
        // Skip if already loaded or no video attachment
        if (!item.mediaCenter.vidioAttachments || this.videoDataMap[id]) { return; }
        const url = `${AppConsts.remoteServiceBaseUrl}/api/MediaLibrary/GetMediaForAnonymous?fileToken=${item.mediaCenter.vidioAttachments}`;
        this._http.get<any>(url).subscribe(result => {
            if (result && result.result && result.result.fileContent) {
                this.videoDataMap[id] = this._sanitizer.bypassSecurityTrustResourceUrl(result.result.fileContent);
            }
        }, error => {
            console.error('Failed to load video', error);
        });
    }

    closeGallery(): void {
        this.galleryOpen = false;
        document.body.style.overflow = '';
    }

    galleryPrev(): void {
        this.galleryIndex = (this.galleryIndex - 1 + this.galleryItems.length) % this.galleryItems.length;
        // Lazily load the video for the newly visible item
        this.loadVideoForGalleryItem(this.galleryIndex);
    }

    galleryNext(): void {
        this.galleryIndex = (this.galleryIndex + 1) % this.galleryItems.length;
        // Lazily load the video for the newly visible item
        this.loadVideoForGalleryItem(this.galleryIndex);
    }

    get currentGalleryItem(): GetMediaCenterForViewDto {
        return this.galleryItems[this.galleryIndex];
    }

    get currentGalleryImage(): string {
        if (!this.currentGalleryItem) { return ''; }
        return this.getImage(this.currentGalleryItem);
    }

    get currentGalleryVideo(): SafeResourceUrl {
        if (!this.currentGalleryItem) { return ''; }
        return this.getVideo(this.currentGalleryItem);
    }

    get currentIsVideo(): boolean {
        if (!this.currentGalleryItem) { return false; }
        return this.isVideo(this.currentGalleryItem);
    }
}
